import { history } from '@cobliteam/store';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const TRACKING_EVENT_ID_PARAM = 'eventId';
const LEGACY_EVENT_TYPE_PARAM = 'event_type';
const LEGACY_EVENT_TIME_PARAM = 'event_time';
const LEGACY_EVENT_STEP_PARAM = 'event_step';
const LEGACY_EVENT_ID_PARAM = 'event_id';

const LEGACY_EVENT_PARAMS = [LEGACY_EVENT_TYPE_PARAM, LEGACY_EVENT_TIME_PARAM, LEGACY_EVENT_STEP_PARAM, LEGACY_EVENT_ID_PARAM];

type TrackingMapFallbackEventType = 'incident' | 'speed' | 'stop';

export const buildTrackingMapFallbackEventId = (eventType: TrackingMapFallbackEventType, eventKey: number | string) => `${eventType}:${eventKey}`;

const getLegacyTrackingMapEventId = (params: URLSearchParams) => {
  const eventType = params.get(LEGACY_EVENT_TYPE_PARAM);

  if (eventType === 'geofence') {
    return params.get(LEGACY_EVENT_ID_PARAM);
  }

  if (eventType === 'incident' || eventType === 'speed') {
    const eventTime = params.get(LEGACY_EVENT_TIME_PARAM);
    return eventTime ? buildTrackingMapFallbackEventId(eventType, eventTime) : null;
  }

  if (eventType === 'stop') {
    const eventStep = Number(params.get(LEGACY_EVENT_STEP_PARAM));
    return Number.isFinite(eventStep) ? buildTrackingMapFallbackEventId('stop', eventStep - 1) : null;
  }

  return null;
};

export const getTrackingMapEventIdFromSearch = (search: string) => {
  const params = new URLSearchParams(search);
  return params.get(TRACKING_EVENT_ID_PARAM) ?? getLegacyTrackingMapEventId(params);
};

export const isCurrentTrackingMapEventIdSelected = (eventId: string) => getTrackingMapEventIdFromSearch(history.location.search) === eventId;

export const useTrackingMapEventSelection = (eventId: string, onSelected: () => void) => {
  const { search } = useLocation();
  const selectedEventId = getTrackingMapEventIdFromSearch(search);

  useEffect(() => {
    if (selectedEventId === eventId) {
      onSelected();
    }
  }, [eventId, onSelected, selectedEventId]);
};

export const replaceCurrentTrackingMapEventId = (eventId: string) => {
  // Read the live browser URL: nuqs-driven filters (start_time/end_time) write directly
  // via window.history.replaceState and never reach @cobliteam/store's history snapshot,
  // so history.location.search would be stale and overwrite them on the next replace.
  const params = new URLSearchParams(window.location.search);
  params.set(TRACKING_EVENT_ID_PARAM, eventId);

  for (const legacyParam of LEGACY_EVENT_PARAMS) {
    params.delete(legacyParam);
  }

  history.replace({ search: `?${params.toString()}` });
};
